import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { inboxItemSchema, type InboxItem } from '@secretario/shared';
import { isNativeShell } from './platform.js';

/**
 * Avisos de outros apps (WhatsApp, Telegram, SMS...), só no app Android e só depois
 * que a pessoa liga o acesso a notificações. O aparelho lê quem escreveu e o texto;
 * nada fica guardado nem sai do aparelho sem um pedido explícito (sugestão, voz).
 * A resposta vai pelo campo do próprio aviso, sem abrir o outro app.
 */
type InboxPlugin = {
  status(): Promise<{ granted: boolean; connected: boolean }>;
  openSettings(): Promise<void>;
  list(): Promise<{ items: unknown[] }>;
  reply(options: { key: string; text: string }): Promise<{ sent: boolean }>;
  dismiss(options: { key: string }): Promise<{ done: boolean }>;
  addListener(
    event: 'inboxMessage',
    listener: (item: unknown) => void,
  ): Promise<PluginListenerHandle>;
};

const plugin = registerPlugin<InboxPlugin>('SecretarioInbox');

const ON_KEY = 'secretario.avisos.outros';
const DRAFT_KEY = 'secretario.avisos.sugerir';
const SEEN_KEY = 'secretario.avisos.visto';
/** A chave mudou (em Conta › Notificações): quem vigia a barra confere de novo. */
export const INBOX_CONFIG_EVENT = 'secretario:inbox-config';

export function inboxSupported(): boolean {
  return isNativeShell() && Capacitor.getPlatform() === 'android';
}

async function quiet<T>(call: () => Promise<T>): Promise<T | null> {
  if (!inboxSupported()) return null;
  try {
    return await call();
  } catch {
    return null;
  }
}

const flag = (key: string) => {
  try {
    return window.localStorage.getItem(key) === 'sim';
  } catch {
    return false;
  }
};
const setFlag = (key: string, on: boolean) => {
  try {
    if (on) window.localStorage.setItem(key, 'sim');
    else window.localStorage.removeItem(key);
  } catch {
    /* sem memória */
  }
  window.dispatchEvent(new CustomEvent(INBOX_CONFIG_EVENT));
};

/** A pessoa ligou a leitura neste aparelho (além da permissão do Android). */
export const inboxWanted = () => flag(ON_KEY);
export const setInboxWanted = (on: boolean) => setFlag(ON_KEY, on);
/** Fase 2: ela deixou o Secretário sugerir respostas (o texto do aviso vai à IA na hora). */
export const inboxDraftWanted = () => flag(DRAFT_KEY);
export const setInboxDraftWanted = (on: boolean) => setFlag(DRAFT_KEY, on);

/** A leitura está de pé: ligada aqui e permitida no Android. */
export async function inboxActive(): Promise<boolean> {
  if (!inboxWanted()) return false;
  const status = await quiet(() => plugin.status());
  if (!status?.granted) return false;
  // Permitiu agora nas configurações: o ouvinte do Android leva instantes para religar.
  if (!status.connected) await new Promise((resolve) => setTimeout(resolve, 400));
  return true;
}

export const inboxStatus = () => quiet(() => plugin.status());
export const openInboxSettings = () => quiet(() => plugin.openSettings());

export async function listInbox(): Promise<InboxItem[]> {
  const result = await quiet(() => plugin.list());
  return (result?.items ?? [])
    .map((item) => inboxItemSchema.safeParse(item))
    .filter((parsed) => parsed.success)
    .map((parsed) => parsed.data)
    .sort((a, b) => b.at - a.at);
}

export async function replyInbox(key: string, text: string): Promise<boolean> {
  const result = await quiet(() => plugin.reply({ key, text }));
  return result?.sent === true;
}

export const dismissInbox = (key: string) => quiet(() => plugin.dismiss({ key }));

/** Mensagem nova chegou (com o app aberto). */
export async function watchInbox(onMessage: (item: InboxItem) => void): Promise<() => void> {
  if (!inboxSupported()) return () => {};
  try {
    const handle = await plugin.addListener('inboxMessage', (raw) => {
      const parsed = inboxItemSchema.safeParse(raw);
      if (parsed.success) onMessage(parsed.data);
    });
    return () => void handle.remove();
  } catch {
    return () => {};
  }
}

/** Até quando a pessoa já viu (ms): o que chegou depois é "enquanto você estava fora". */
export function inboxSeenAt(): number {
  try {
    return Number(window.localStorage.getItem(SEEN_KEY) ?? 0);
  } catch {
    return 0;
  }
}
export function markInboxSeen(at = Date.now()): void {
  try {
    window.localStorage.setItem(SEEN_KEY, String(at));
  } catch {
    /* sem memória */
  }
}

/** "Há 2 min", "Há 1 h", "Ontem". */
export function inboxAgo(at: number, now = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - at) / 60_000));
  if (minutes < 1) return 'Agora';
  if (minutes < 60) return `Há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Há ${hours} h`;
  return hours < 48 ? 'Ontem' : `Há ${Math.round(hours / 24)} dias`;
}
